import React, { FC, Fragment, useCallback, useState } from 'react';
import { Alert, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useQuery } from '@apollo/client';
import { getCategoriesTemplate } from 'apollo/templates/action';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import CategoriesPageActions from './categories-page-actions.component';
import CategoryItem from './category-item.component';
import AddCategoryDialog from './add-category-dialog/add-category-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import EditCategoryDialog from './edit-category-dialog/edit-category-dialog.component';

const Categories: FC = () => {
    const { t: translate } = useTranslation(['lab-settings', 'orders']);
    const [categories, setCategories] = useState<{ _id: string; name: string }[]>([]);
    const [filters, setFilters] = useState<{ freetext: string }>({ freetext: '' });
    const [categoryToEdit, setCategoryToEdit] = useState<{ _id: string; name: string }>(
        {} as { _id: string; name: string },
    );
    const { showBoundary } = useDenthubErrorBoundary();
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState({ addedCategory: false, updatedCategory: false });

    const { loading: categoriesLoading } = useQuery(getCategoriesTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            filters: filters,
        },
        onCompleted({ categories }) {
            setCategories(categories);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFilterChange = useCallback(
        debounce((filters: { freetext: string }) => {
            setFilters({ freetext: filters.freetext });
        }, 300),
        [],
    );

    const handleEditCategory = (category: { _id: string; name: string }) => {
        setCategoryToEdit(category);
        setEditDialogOpen(true);
    };

    const editCategory = (category: { _id: string; name: string }) => {
        const categoryArr = [...categories];
        const index = categoryArr.findIndex((cat: { _id: string; name: string }) => cat._id === category._id);
        if (index > -1) {
            categoryArr.splice(index, 1, category);
        }
        setCategories([...categoryArr]);
        setSnackbarOpen({ ...snackbarOpen, updatedCategory: true });
    };

    return (
        <Fragment>
            <CategoriesPageActions
                onNewCategoryClick={() => setAddDialogOpen(true)}
                onFilterChange={handleFilterChange}
            />
            {categoriesLoading ? (
                <Loader />
            ) : (
                <Fragment>
                    {!categories.length ? (
                        <Box mt={2}>
                            <Alert severity="info">{translate('lab-settings:NO_CATEGORIES')}</Alert>
                        </Box>
                    ) : (
                        <Paper variant="outlined">
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{translate('orders:CATEGORY')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {categories.map((category, index) => (
                                            <CategoryItem
                                                key={index}
                                                category={category}
                                                onCategoryRowClick={handleEditCategory}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                    <AddCategoryDialog
                        dialogOpen={addDialogOpen}
                        onCloseDialog={() => setAddDialogOpen(false)}
                        onAddCategory={(category: { _id: string; name: string }) => {
                            setSnackbarOpen({ ...snackbarOpen, addedCategory: true });
                            setCategories([...categories, category]);
                        }}
                    />
                    <EditCategoryDialog
                        dialogOpen={editDialogOpen}
                        onCloseDialog={() => setEditDialogOpen(false)}
                        categoryToEdit={categoryToEdit}
                        onEditCategory={editCategory}
                    />
                    {snackbarOpen.addedCategory ? (
                        <AlertSnackbar type="success" message={translate('lab-settings:CATEGORY_ADDED')} />
                    ) : undefined}
                    {snackbarOpen.updatedCategory ? (
                        <AlertSnackbar type="success" message={translate('lab-settings:CATEGORY_UPDATED')} />
                    ) : undefined}
                </Fragment>
            )}
        </Fragment>
    );
};

export default Categories;
